import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { resetPasswordSchema } from "./schemas/authenticationSchema";

import Button from "../../components/Button";
import Input from "../../components/Input";

import { resetPassword } from "../../api/authRequest";
import { useTranslation } from "../../i18n/TranslationProvider";

const ResetPassword = () => {
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const { t } = useTranslation();

    const navigate = useNavigate();

    const { search } = useLocation();
    const token = new URLSearchParams(search).get("token");

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
    } = useForm({
        resolver: yupResolver(resetPasswordSchema),
    });

    const onSubmit = async ({ password }) => {
        setError("");

        if (!token) {
            setError(t("auth.resetTokenMissing"));
            return;
        }

        try {
            await resetPassword({ token, password });
            setSuccess(true);
            setTimeout(() => {
                navigate("/login");
            }, 2000);
        } catch (err) {
            const status = err.response?.status;
            if (status === 400 || status === 403) {
                setError(t("auth.resetLinkInvalid"));
            } else {
                setError(t("auth.resetPasswordErrorGeneric"));
            }
        }
    };

    return (
        <>
            <div className="custom-container mx-auto w-4/5 lg:w-1/2">
                <div className="text-base md:text-2xl text-center mx-auto mb-6">
                    <h4 className="mb-2">{t("auth.resetPassword")}</h4>
                    <p>{t("auth.resetPasswordSubheading")}</p>
                </div>
                <form
                    className="flex flex-col gap-4"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <Input
                        label={t("auth.newPasswordLabel")}
                        name="password"
                        type="password"
                        register={register}
                        errors={errors}
                        placeholder={t("auth.newPasswordPlaceholder")}
                    />
                    <Input
                        label={t("auth.confirmNewPasswordLabel")}
                        name="confirmPassword"
                        type="password"
                        register={register}
                        errors={errors}
                        placeholder={t("auth.confirmNewPasswordPlaceholder")}
                    />
                    {error && <p className="text-red-600">{error}</p>}
                    {success && !error && (
                        <p className="text-teal-600 font-semibold">
                            {t("auth.resetPasswordSuccess")}
                        </p>
                    )}
                    <Button
                        type="submit"
                        isLoading={isSubmitting}
                        disabled={isSubmitting || success}
                    >
                        {t("auth.resetPassword")}
                    </Button>
                </form>
                <Button
                    className="mt-4"
                    onClick={() => navigate("/login")}
                    variant="outline"
                >
                    {t("auth.back")}
                </Button>
                <div className="mt-4 px-2 flex justify-between items-center">
                    <p>{t("auth.needHelp")}</p>
                    <h6
                        className="text-teal-600 cursor-pointer"
                        onClick={() => navigate("/help")}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                navigate("/help");
                            }
                        }}
                    >
                        {t("auth.contactUs")}
                    </h6>
                </div>
            </div>
        </>
    );
};

export default ResetPassword;